import React from 'react';
import streamingStartChangelog from './streaming-start.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import {
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
  StreamingStartNodeState,
} from '../..';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Streaming Start',
  identifier: 'StreamingStart',
  command_id: 'streaming.start',
  icon: 'telnyx',
  background: '#000000',
  changelog: streamingStartChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'streaming.start.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'streaming.start.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      // id needs to match the event_type of the webhook
      id: 'streaming.started',
      label: 'streaming.started',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'streaming.started.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'streaming.stopped',
      label: 'streaming.stopped',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'streaming.stopped.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'streaming.failed',
      label: 'streaming.failed',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'streaming.failed.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'streaming.started',
      expected: false,
      multi: false,
    },
    {
      webhook: 'streaming.stopped',
      expected: false,
      multi: false,
    },
    {
      webhook: 'streaming.failed',
      expected: false,
      multi: false,
    },
  ],
};

export const state: StreamingStartNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  client_state: { type: NodeInputType.Expression, value: '' },
  // streaming
  stream_url: { type: NodeInputType.Expression, value: '' },
  stream_track: 'inbound_track',
  stream_bidirectional_mode: 'mp3',
  stream_bidirectional_codec: 'PCMU',
  stream_bidirectional_target_legs: 'opposite',
  // dialogflow
  enable_dialogflow: 'false',
  analyze_sentiment: 'false',
  partial_automated_agent_reply: 'false',
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
  {
    id: 'stream_url',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Stream URL',
    description: 'The destination WebSocket address where the stream is going to be delivered.',
    placeholder: 'wss://example.com',
    required: false,
  },
  {
    id: 'stream_track',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    options: [
      { value: 'inbound_track', label: 'Inbound Track' },
      { value: 'outbound_track', label: 'Outbound Track' },
      { value: 'both_tracks', label: 'Both Tracks' },
    ],
    label: 'Stream Track',
    description: 'Specifies which track should be streamed.',
    required: false,
  },
  {
    id: 'stream_bidirectional_mode',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    options: [
      { value: 'mp3', label: 'MP3' },
      { value: 'rtp', label: 'RTP' },
    ],
    label: 'Stream Bidirectional Mode',
    description: 'Configures method of bidirectional streaming (mp3, rtp).',
    required: false,
  },
  {
    id: 'stream_bidirectional_codec',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    options: [
      { value: 'PCMU', label: 'PCMU' },
      { value: 'PCMA', label: 'PCMA' },
      { value: 'G722', label: 'G722' },
    ],
    label: 'Stream Bidirectional Codec',
    description: 'Indicates codec for bidirectional streaming RTP payloads.',
    required: false,
  },
  {
    id: 'stream_bidirectional_target_legs',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    options: [
      { value: 'opposite', label: 'Opposite' },
      { value: 'both', label: 'Both' },
      { value: 'self', label: 'Self' },
    ],
    label: 'Stream Bidirectional Target Legs',
    description: 'Specifies which call legs should receive the bidirectional stream audio.',
    required: false,
  },
  {
    id: 'enable_dialogflow',
    type: NodeFieldType.Toggle,
    label: 'Enable Dialogflow',
    description:
      'Dialogflow is a natural language understanding platform that makes it easy to design and integrate a conversational user interface into your mobile app, web application, device, bot, interactive voice response system, and so on.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'analyze_sentiment',
    tab: 'dialogflow',
    type: NodeFieldType.Toggle,
    label: 'Dialogflow Analyze Sentiment',
    description: 'Enable sentiment analysis from Dialogflow.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'partial_automated_agent_reply',
    tab: 'dialogflow',
    type: NodeFieldType.Toggle,
    label: 'Dialogflow Partial Automated Agent Reply',
    description: 'Enable partial automated agent reply from Dialogflow.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
];

export const output: NodeOutput = {
  default: {
    'streaming.started': {
      streaming: {
        started: {
          call_control_id: 'string',
          connection_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          stream_id: 'string',
          stream_params: {
            stream_url: 'string',
            track: "'inbound_track' | 'outbound_track' | 'both_tracks'",
          },
          stream_type: "'websocket' | 'dialogflow'",
        },
      },
    },
    'streaming.stopped': {
      streaming: {
        stopped: {
          call_control_id: 'string',
          connection_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          stream_id: 'string',
          stream_params: {
            track: "'inbound_track' | 'outbound_track' | 'both_tracks'",
            stream_url: 'string',
          },
          stream_type: "'websocket' | 'dialogflow'",
        },
      },
    },
    'streaming.failed': {
      streaming: {
        failed: {
          call_control_id: 'string',
          connection_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          failure_reason: 'string',
          stream_id: 'string',
          stream_params: {
            stream_url: 'string',
            track: "'inbound_track' | 'outbound_track' | 'both_tracks'",
          },
          stream_type: "'websocket' | 'dialogflow'",
        },
      },
    },
  },
  dynamic: null,
};

export const StreamingStart: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'streaming.start') return;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
